import analyticsMixin from "@/mixins/analytics-mixin";
import { routeData } from "@/router/constants/routes";
import router from "@/router";
import store from "@/store";
import { storeActions } from "@/constants/store-actions";
import { sessionStorageKeyConstants } from "@/constants/session-storage";
import { deleteFunnelCookie } from "@/helpers/heritage-integration/cookie-helper";
import { clearBeforeEachErrorRecoveryCount } from "@/router/methods/before-each-error-recovery";
import experimentMixin from "../../mixins/experiment-mixin";

const STATIC_ERROR_PAGE_PATH = "/fmg/static/error";

/** Clears sessionStorage keys that can re-poison the funnel after a bailout. */
export function clearBailoutSessionStorage() {
    clearBeforeEachErrorRecoveryCount();

    try {
        window.sessionStorage?.removeItem(sessionStorageKeyConstants.SUBMITTED_STATE);
        window.sessionStorage?.removeItem(
            sessionStorageKeyConstants.HERITAGE_SUPPRESS_REDIRECT_COUNT
        );
        window.sessionStorage?.removeItem(sessionStorageKeyConstants.EXTERNAL_PARAMETER_STATE);
    } catch {
        // sessionStorage unavailable
    }
}

export async function redirectToStaticErrorPage(errorPayload) {
    await logErrorToDataLayerAndLogger("redirectToStaticErrorPage", errorPayload, null);
    clearBailoutSessionStorage();

    // Prefer top-level navigation so the static page loads outside any iframe wrapper.
    // Assigning window.top.location throws SecurityError when cross-origin; fall back to this frame.
    try {
        window.top.location = STATIC_ERROR_PAGE_PATH;
    } catch {
        window.location = STATIC_ERROR_PAGE_PATH;
    }
}

export async function handleSoftError(errorPayload, forceRestart = false) {
    if (forceRestart) {
        await store.dispatch(storeActions.RESET_STATE);
        deleteFunnelCookie();
    }

    logErrorToDataLayerAndLogger("handleSoftError", errorPayload, forceRestart);

    router.push({
        name: routeData.ERROR.name,
    });
}

export async function handleHardError(errorPayload) {
    try {
        const isInStaticErrorExperiment = experimentMixin.methods.hasSettingEqualTo(
            "UseStaticErrorPage",
            "true"
        );

        if (isInStaticErrorExperiment) {
            await redirectToStaticErrorPage(errorPayload);
            return;
        } else {
            await handleSoftError(errorPayload, true);
            return;
        }
    } catch (exception) {
        await handleSoftError(errorPayload, true);
    }
}

async function logErrorToDataLayerAndLogger(context, errorPayload, forceRestart = null) {
    const forceRestartSegment = forceRestart === true ? " (force restart)" : "";
    const message = `${context}${forceRestartSegment}: ${errorPayload?.cause ?? "unknown cause"}`;
    analyticsMixin?.methods?.pushPageErrorToDataLayer(errorPayload);
    global.$logger.logError(message, errorPayload);
}
